/**
 * `<DataList>` — the one table, rebuilt to list engine v2.
 *
 * Four ideas do most of the work, and each of them is a decision that shows up
 * everywhere once it is made:
 *
 * **Read by shape first.** Every column wears an icon; every row carries a
 * landing mark on the left — a tile, a face, a map square. A screen
 * full of different objects still looks like one product because the marks all
 * come from the same eight colour pairs.
 *
 * **The header is the control surface.** Sort, resize, reorder, pin, hide,
 * group and summarize all start from the column header. The toolbar repeats
 * the global versions for discoverability, but nobody who knows the table has
 * to leave it.
 *
 * **Wide is fine; lost is not.** The mark column and the action cell are
 * pinned, so however far the table scrolls sideways you can still tell which
 * row you are on and still act on it.
 *
 * **Every width is a real number.** Not `fr`, not `minmax`: an explicit pixel
 * width per column. That is what makes resizing, saving a layout and sticky
 * positioning all work — and `fr` columns cannot overflow their container, so
 * a table with twenty columns would have squeezed them all to nothing rather
 * than scrolling.
 */
import {
  Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode,
} from 'react'
import type { Density, FieldIcon } from '@rowted/shared'
import { FIELD_ICON_MEANINGS } from '@rowted/shared'
import { ContextMenu, type MenuItem } from './context-menu.js'
import { cn } from '../lib/cn.js'

// Re-exported: half the app imports `MenuItem` from here, and the menu moving
// into its own module is not a reason to touch seven call sites.
export { ContextMenu } from './context-menu.js'
export type { MenuItem } from './context-menu.js'
import { Checkbox } from './primitives.js'
import { Popover } from './overlays.js'
import { COLUMN_ICONS, IconChevronDown, IconChevronRight, IconMore, IconPin } from '../frame/icons.js'

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

export interface DataListColumn<T> {
  key: string
  header: string
  icon?: FieldIcon
  /**
   * A number: tabular figures, and a narrower default width.
   *
   * Not right-aligned. §3.2 asks for right alignment and the mockups draw it,
   * but a real board mixes text and money across twelve columns and the eye
   * loses the left edge — so every column reads from the same place, and the
   * tabular figures do the work of keeping the digits in line.
   */
  numeric?: boolean
  /** Explicit width in pixels. A width saved on the view wins over it. */
  width?: number
  render: (row: T) => ReactNode
  /**
   * The Σ value for this column.
   *
   * Takes which group it is rendering — `null` for the footer's grand total —
   * because the server computes both, and a function that only knew the rows
   * in front of it could only re-total the page. Every group row showed the
   * grand total until this argument existed, which reads as wrong data rather
   * than as a missing feature.
   */
  aggregate?: (context: { rows: T[]; groupKey: string | null }) => ReactNode
  /**
   * Words under a text column in the footer — "22 companies", "7 need action".
   * §3.5's footer counts as well as sums, and a count of distinct customers is
   * not something Σ can express.
   */
  footer?: (rows: T[]) => ReactNode
  /** How this column is sorted, when it is. `undefined` means it cannot be. */
  sort?: 'asc' | 'desc' | null
  /** Its place in a multi-column sort: the small "2" beside the arrow. */
  sortRank?: number
}

export interface DataListGroup<T> {
  key: string
  label: string
  rows: T[]
  /** The group's real size, when the page shows only part of it. */
  total?: number
}

export interface DataListSelection {
  /**
   * Asked per row rather than handed a set, because the set is sometimes not
   * a set: "all 128 that match" is a *mode* with exceptions (v3 §2.1), and a
   * table that read `selected.has(id)` could not draw it without the client
   * first materialising 128 ids it does not have.
   */
  isSelected: (key: string) => boolean
  /** Whether anything at all is ticked — what puts phone cards in select mode. */
  anySelected: boolean
  onToggle: (key: string) => void
  /** ⇧-click: everything between the last tick and this one. */
  onToggleRange?: (fromKey: string, toKey: string) => void
  /**
   * The header box, and the menu beside it.
   *
   * The box ticks this page; the caret opens the three choices that a
   * checkbox cannot express — this page, everything that matches, none
   * (§2.5). `checked` and `indeterminate` are computed from the rows on
   * screen whichever mode the selection is in, because that is the question
   * the box is answering.
   */
  header: {
    checked: boolean
    indeterminate: boolean
    onToggle: () => void
    menu: ReactNode
  }
}

export interface DataListProps<T> {
  columns: DataListColumn<T>[]
  groups: DataListGroup<T>[]
  rowKey: (row: T) => string
  selection?: DataListSelection
  density?: Density
  /**
   * A ceiling on the table's height, in pixels — a list inside a record page.
   *
   * When it is set the scroller owns *both* scrollbars rather than only the
   * sideways one, which is what makes the sticky header stick: `position:
   * sticky` is relative to the nearest scrolling ancestor, and with the
   * vertical scroll on a wrapper outside this element the header scrolled away
   * with the rows. It is also what keeps a wide table's sideways scroll inside
   * the card instead of widening the page.
   */
  maxHeight?: number

  /*
   * There is no `stripe`. It was a 4px colour line at the row's left edge, and
   * the Jobs board was the only list that drew one — the one mark in the app
   * that said nothing about *what* the row is, only faintly about its status,
   * in the place the eye goes last. `jobMark` replaced it with the division's
   * tile and a status dot (`docs/list-marks.md` §1), and with that the slot had
   * no second caller and no argument for existing.
   */
  /**
   * The mark on the left of every row: an avatar, a map square, a photo.
   * Pinned, so it is still there when the table has scrolled sideways — it is
   * how you know which row you are looking at.
   */
  mark?: {
    width?: number
    render: (row: T) => ReactNode
  }
  /** What the expand chevron opens. Rows have no chevron without it. */
  tray?: (row: T) => ReactNode

  /** `additive` is a ⇧-click: add a second sort rather than replacing. */
  /**
   * Widths somebody dragged, from the saved view.
   *
   * Separate from a column's own `width` because the two must not be confused
   * by the fit: a screen's suggestion is a starting point the measurement is
   * allowed to overrule, and a width a person set with their own hands is not.
   */
  userWidths?: Record<string, number>
  onSort?: (key: string, additive: boolean) => void
  /** Where the insertion line is: before the column at this index. */
  onReorder?: (key: string, before: number) => void
  onResize?: (key: string, width: number) => void
  /** The header's right-click menu. */
  headerMenu?: (key: string) => MenuItem[]
  rowMenu?: (row: T) => MenuItem[]
  /** Columns the user pinned left, beyond the mark. */
  pinned?: string[]

  onRowClick?: (row: T) => void
  selectedKey?: string
  showFooter?: boolean
  emptyState?: ReactNode
  /** Skeleton rows on first load — never a spinner over the table. */
  loading?: boolean
  /**
   * What a row looks like when there is no room for a table (phones).
   *
   * Given this, the engine stops drawing a grid and draws one card per row.
   * It is a set of column keys rather than a second set of renderers: the
   * card uses the very same `render` the table would have used, so a status
   * chip, a landing mark and a money column look the same on a phone as they
   * do on a laptop, and no screen has to describe itself twice.
   */
  cards?: DataListCards<T>
}

/**
 * **The third row of a card, as data rather than as markup.**
 *
 * The screen says what belongs on the row; the engine draws it — the chip
 * first, the facts after it with a dot between each, the date hard right.
 * The alternative is five pages each composing their own `<span>`s, which is
 * five answers to "what does a card's bottom row look like" and five places
 * for the separators, the truncation and the alignment to drift.
 *
 * Every part is optional and an empty one takes its separator with it, so a
 * contact with no company reads as its chip and its date rather than as a
 * line of punctuation.
 */
export interface CardLine {
  /** The status chip, as the row's own `StatusChip` — never truncated. */
  chip?: ReactNode
  /** Dot-separated after it. The group truncates; the chip and date do not. */
  facts?: ReactNode[]
  /** Hard right, and never truncated: when this row last did anything. */
  trailing?: ReactNode
}

/**
 * Which column plays which part on a card — plan §3.7's phone rules applied
 * to §3.5's one list engine.
 *
 * Three rows of a card, in reading order: the name, its own second line, then
 * a line of facts. Everything is optional but `title`, because a card with no
 * name is a card nobody can identify.
 */
export interface DataListCards<T = unknown> {
  /** The big line, and the muted one under it: the primary column. */
  title: string
  /** Drawn first on the third row, as the column already renders it. */
  chip?: string
  /** Up to three more values on that row, in this order. */
  meta?: string[]
  /** Pushed to the right of the third row — a date, almost always. */
  trailing?: string
  /**
   * What this row *is*, where it sits and who it belongs to — written by the
   * screen rather than assembled from columns (v3 §8).
   *
   * The generic row is the best a list engine can do on its own: it takes
   * whichever columns the view happens to be showing and lines their values
   * up. That is right for a table, where a heading says what each value is,
   * and thin on a card, where three anonymous facts say less than one
   * sentence.
   *
   * Where it is given it **replaces** the chip-and-meta row. Where it is not,
   * the generic row stands, so no list has to supply one to keep working.
   *
   * It returns a `CardLine` — parts, not markup — so the dots, the
   * truncation and the right-alignment are decided once here rather than
   * five times across five pages.
   */
  line?: (row: T) => CardLine | null
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

/** Row height and cell type, per density (v2 principle 2). */
const DENSITY = {
  compact: { row: 44, text: 'text-cell', sub: 'text-sub' },
  default: { row: 52, text: 'text-row', sub: 'text-rowsub' },
  spacious: { row: 60, text: 'text-row', sub: 'text-rowsub' },
} as const

const MARK_WIDTH = 44
const ACTIONS_WIDTH = 68
/**
 * The tick column, wide enough for the box **and** the caret beside it.
 *
 * 36 when it held only a checkbox. The caret is what makes "select all that
 * match" reachable at all (§2.5), and 52 is the box at its old x with a
 * 20px control to its right — so every row's checkbox stays exactly where it
 * was and only the header gained something.
 */
const TICK_WIDTH = 52
const MIN_WIDTH = 48

/**
 * How wide the header's ⋯ button needs, reserved during the fit.
 *
 * It is absolutely positioned so it contributes nothing to the header's own
 * layout — which is right, it should not take 16px from a column that has
 * room to spare. But a column fitted to exactly its label would have that
 * button sitting on top of the last two letters, so the fit reserves the space
 * even though the layout does not.
 */
const HEADER_MENU_RESERVE = 22

/** Nothing is fitted narrower than this, or wider. */
const MIN_FIT = 64
const MAX_FIT = 360

/**
 * The width to use before anything has been measured.
 *
 * One frame, at most: the fit runs in a layout effect, so this is what the
 * grid is laid out at while it is being measured and never what anybody sees.
 */
const widthFor = <T,>(column: DataListColumn<T>): number => {
  const wanted = column.width ?? (column.numeric ? 128 : 184)
  return column.header ? Math.max(76, wanted) : wanted
}

// ---------------------------------------------------------------------------

export function DataList<T>({
  columns, groups, rowKey, mark, tray, selection, density = 'default',
  onSort, onReorder, onResize, headerMenu, rowMenu, pinned = [], userWidths,
  onRowClick, selectedKey, showFooter = true, emptyState, loading = false, maxHeight,
  cards,
}: DataListProps<T>) {
  const metrics = DENSITY[density]
  const rows = groups.flatMap((group) => group.rows)

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ items: MenuItem[]; x: number; y: number } | null>(null)
  const [focused, setFocused] = useState<string | null>(null)
  const lastTicked = useRef<string | null>(null)

  /*
   * Where each row sits in the whole list, so ↑↓ can cross a group boundary.
   * Built once rather than by `indexOf` per row: that was O(n²) on a 200-row
   * page and quietly wrong, because a row's index inside its group is not its
   * index in the list.
   */
  const orderOf = new Map(rows.map((row, index) => [rowKey(row), index]))

  const scroller = useRef<HTMLDivElement>(null)
  const drag = useColumnDrag(columns, onReorder)
  const resize = useColumnResize(onResize)

  /*
   * What the fit is measuring, so it runs again when the rows change and not
   * on every render. The first and last row's keys plus the count is enough to
   * notice a new page, a new sort or a new filter without hashing the data.
   */
  const rowsKey = `${rows.length}:${rows[0] ? rowKey(rows[0]) : ''}`
    + `:${rows.length > 1 ? rowKey(rows[rows.length - 1]!) : ''}`
  const fit = useColumnFit(columns, density, rowsKey)

  /**
   * How wide a column actually is.
   *
   * In order: what the pointer is dragging right now, then a width somebody
   * set and saved, then what the fit measured, then the screen's suggestion.
   * The fit sits under a saved width and over a suggestion, which is the whole
   * rule: measuring beats guessing, and a person beats both.
   */
  const widthOf = (column: DataListColumn<T>): number =>
    resize.widthOf(column.key, userWidths?.[column.key] ?? fit.widthOf(column.key)
      ?? widthFor(column))

  /*
   * The grid, left to right: tick box, mark, the columns, a spacer that eats
   * the slack, then the pinned action cell. The spacer is what keeps the
   * actions against the right edge on a narrow table without them floating
   * away from the data on a wide one.
   */
  const template = [
    selection ? `${TICK_WIDTH}px` : null,
    mark ? `${mark.width ?? MARK_WIDTH}px` : null,
    // `max-content` for the one pre-paint render the fit measures.
    ...columns.map((column) => (fit.measuring ? 'max-content' : `${widthOf(column)}px`)),
    'minmax(0, 1fr)',
    rowMenu || tray ? `${ACTIONS_WIDTH}px` : null,
  ].filter(Boolean).join(' ')

  /* While measuring a cell must be as wide as it wants, not clipped to fit. */
  const cellFit = fit.measuring ? 'whitespace-nowrap' : 'min-w-0 truncate'

  /** Where a pinned column sits once the table has scrolled under it. */
  const leftOffset = (index: number): number => {
    let offset = (selection ? TICK_WIDTH : 0) + (mark ? (mark.width ?? MARK_WIDTH) : 0)
    for (let i = 0; i < index; i += 1) {
      if (pinned.includes(columns[i]!.key)) offset += widthOf(columns[i]!)
    }
    return offset
  }

  /** Where the mark column sits once the table has scrolled under it. */
  const markLeft = selection ? TICK_WIDTH : 0
  const tickLeft = 0


  const openMenu = (items: MenuItem[], event: { clientX: number; clientY: number }) => {
    if (items.length === 0) return
    setMenu({ items, x: event.clientX, y: event.clientY })
  }

  /** ↑↓ to move, ⏎ to open, space to tick — v2's keyboard contract. */
  const onRowKeyDown = useCallback((event: React.KeyboardEvent, row: T, index: number) => {
    const key = rowKey(row)
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const next = rows[index + (event.key === 'ArrowDown' ? 1 : -1)]
      if (next) setFocused(rowKey(next))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      onRowClick?.(row)
      return
    }
    if (event.key === ' ') {
      event.preventDefault()
      selection?.onToggle(key)
    }
  }, [rows, rowKey, onRowClick, selection])

  useEffect(() => {
    if (!focused) return
    scroller.current?.querySelector<HTMLElement>(`[data-row="${CSS.escape(focused)}"]`)?.focus()
  }, [focused])

  /*
   * How wide the visible part of the table is.
   *
   * The tray needs it. Everything else in here lives inside a `max-content`
   * strip that is as wide as the columns, which is right for a row and wrong
   * for a tray: a tray is about the record, not about the columns, so it is
   * pinned to the left of the *viewport* at the viewport's own width. Without
   * this it was as wide as the table and its fourth panel sat off-screen —
   * behind a horizontal scroll, in a thing somebody opened to avoid scrolling.
   */
  const [viewport, setViewport] = useState(0)
  useEffect(() => {
    const element = scroller.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const measure = () => setViewport(element.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const tick = (row: T, event: React.MouseEvent) => {
    const key = rowKey(row)
    if (event.shiftKey && lastTicked.current && selection?.onToggleRange) {
      selection.onToggleRange(lastTicked.current, key)
    } else {
      selection?.onToggle(key)
    }
    lastTicked.current = key
  }

  /*
   * Cards, when a table is the wrong shape for the room (plan §3.7).
   *
   * Not a second list engine: the same groups, the same `render` from the
   * same columns, the same selection and the same row menu. What changes is
   * the geometry — a row 900px wide becomes a card 358px wide and three lines
   * tall — and that is all it is allowed to change, because the moment a
   * phone has its own renderers the two drift and the phone becomes the
   * version nobody checks.
   *
   * Dropped here, deliberately: the column headers (there is one column), the
   * totals footer (a figure under a column that is not there), horizontal
   * scrolling, resizing, reordering, pinning, and the expand tray. The tray
   * is the one worth naming — on a laptop it is a peek that saves crossing
   * the screen, and on a phone opening the record costs exactly the same tap
   * as opening the tray, so it is a control that buys nothing.
   */
  if (cards) {
    const columnFor = (key?: string) =>
      (key ? columns.find((column) => column.key === key) : undefined)
    const title = columnFor(cards.title) ?? columns[0]
    const chip = columnFor(cards.chip)
    const trailing = columnFor(cards.trailing)
    const meta = (cards.meta ?? [])
      .map((key) => columnFor(key))
      .filter((column): column is DataListColumn<T> => column !== undefined)

    return (
      <div className="flex min-w-0 flex-col gap-2">
        {loading && <CardSkeleton />}

        {!loading && groups.map((group) => {
          const isCollapsed = collapsed.has(group.key)
          return (
            <Fragment key={group.key}>
              {(groups.length > 1 || group.label) && (
                <button
                  type="button"
                  data-group-row={group.key}
                  onClick={() => setCollapsed((current) => {
                    const next = new Set(current)
                    if (next.has(group.key)) next.delete(group.key)
                    else next.add(group.key)
                    return next
                  })}
                  className={cn(
                    'flex h-9 items-center gap-1.5 rounded-button bg-surface-group px-2',
                    'text-left text-[12px] font-bold text-text',
                  )}
                >
                  <span className={cn('shrink-0 text-text-muted transition-transform',
                    isCollapsed && '-rotate-90')}>
                    <IconChevronDown size={14} />
                  </span>
                  {group.label}
                </button>
              )}

              {!isCollapsed && group.rows.map((row) => (
                <RowCard
                  key={rowKey(row)}
                  rowId={rowKey(row)}
                  row={row}
                  title={title}
                  chip={chip}
                  meta={meta}
                  trailing={trailing}
                  line={cards.line}
                  mark={mark}
                  ticked={selection?.isSelected(rowKey(row)) ?? false}
                  selecting={selection?.anySelected ?? false}
                  onToggle={selection ? () => selection.onToggle(rowKey(row)) : undefined}
                  onOpen={onRowClick ? () => onRowClick(row) : undefined}
                  onMenu={rowMenu ? (event) => openMenu(rowMenu(row), event) : undefined}
                />
              ))}
            </Fragment>
          )
        })}

        {!loading && rows.length === 0 && emptyState}

        {menu && (
          <ContextMenu items={menu.items} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />
        )}
      </div>
    )
  }

  return (
    <div className="relative min-w-0">
      {/*
        * One scroller for the whole table, so the header, the rows and the
        * totals footer all move together sideways. A footer that stayed put
        * while the columns moved would line its numbers up under the wrong
        * headings.
        */}
      <div
        ref={scroller}
        /*
          * A stable hook for the one measurement §12 asks for: the table's
          * top edge must be identical with nothing ticked, with three, with
          * fifty and with "all matching". There is no `<table>` to measure —
          * the list is a CSS grid — so the test needs something to hold on
          * to that is not a Tailwind class.
          */
        data-list-table
        style={maxHeight === undefined ? undefined : { maxHeight }}
        className={cn(
          'list-scroll',
          maxHeight === undefined ? 'overflow-x-auto overflow-y-visible' : 'overflow-auto',
        )}
      >
        <div ref={fit.box} className="min-w-full" style={{ width: 'max-content' }}>
          {/* --- header ------------------------------------------------- */}
          <div
            role="row"
            className="sticky top-0 z-30 grid h-list-header items-stretch border-b border-line bg-surface-alt"
            style={{ gridTemplateColumns: template }}
          >
            {selection && (
              <div
                className="sticky z-10 flex items-center gap-0.5 bg-surface-alt pl-3"
                style={{ left: tickLeft }}
              >
                <Checkbox
                  checked={selection.header.checked}
                  indeterminate={selection.header.indeterminate}
                  onChange={selection.header.onToggle}
                  label="Select every row on this page"
                  hideLabel
                />
                <SelectionCaret menu={selection.header.menu} />
              </div>
            )}
            {mark && (
              <div
                aria-hidden
                className="sticky z-10 bg-surface-alt"
                style={{ left: markLeft }}
              />
            )}

            {columns.map((column, index) => (
              <HeaderCell
                key={column.key}
                column={column}
                index={index}
                pinned={pinned.includes(column.key)}
                left={pinned.includes(column.key) ? leftOffset(index) : undefined}
                drag={drag}
                lastIndex={columns.length - 1}
                resize={resize}
                width={widthOf(column)}
                measuring={fit.measuring}
                onRefit={() => fit.refit(column.key)}
                onSort={onSort}
                onMenu={headerMenu ? (event) => openMenu(headerMenu(column.key), event) : undefined}
              />
            ))}

            <div className="bg-surface-alt" />
            {(rowMenu || tray) && (
              <div className="sticky right-0 z-10 border-l border-line-soft bg-surface-alt" />
            )}
          </div>

          {/* --- rows --------------------------------------------------- */}
          {loading && <Skeleton columns={columns.length} template={template} metrics={metrics} />}

          {!loading && groups.map((group) => {
            const isCollapsed = collapsed.has(group.key)
            return (
              <Fragment key={group.key}>
                {(groups.length > 1 || group.label) && (
                  <div
                    role="row"
                    /*
                     * Marked, because "a row of the table" and "the heading
                     * above a batch of them" are different things and the
                     * difference is invisible to a selector that only knows
                     * `role=row`. A test counting rows counted the headings
                     * too, and the first "row" of a grouped Jobs board was a
                     * group label.
                     */
                    data-group-row={group.key}
                    className="sticky z-20 grid h-list-group items-center border-b border-line-soft bg-surface-group"
                    style={{ gridTemplateColumns: template, top: 40 }}
                  >
                    {selection && (
                      <span aria-hidden className="sticky bg-surface-group" style={{ left: tickLeft }} />
                    )}
                    {mark && (
                      <span
                        aria-hidden
                        className="sticky bg-surface-group"
                        style={{ left: markLeft }}
                      />
                    )}

                    {/*
                      * The group's name goes in the first column and its
                      * subtotals go under the columns they are subtotals of.
                      * They have to be laid out against the same template as
                      * the rows: the version that skipped the first two cells
                      * put every subtotal one column to the left of the number
                      * it belonged to, which reads as wrong data rather than
                      * as a layout bug.
                      */}
                    {columns.map((column, columnIndex) => (
                      columnIndex === 0
                        ? (
                          <button
                            key={column.key}
                            type="button"
                            onClick={() => setCollapsed((current) => {
                              const next = new Set(current)
                              if (next.has(group.key)) next.delete(group.key)
                              else next.add(group.key)
                              return next
                            })}
                            /*
                              * Pinned left, always. The group's name is what
                              * tells you which block of rows you are in, and
                              * it used to scroll off the screen with the first
                              * column — so a wide table scrolled to the right
                              * was rows of numbers under headings that had
                              * gone.
                              */
                            className={cn(
                              'sticky z-10 flex min-w-0 items-center gap-1.5 bg-surface-group px-2',
                              'text-left text-[12px] font-bold text-text',
                            )}
                            style={{ left: leftOffset(0) }}
                          >
                            <span className={cn('shrink-0 text-text-muted transition-transform',
                              isCollapsed && '-rotate-90')}>
                              <IconChevronDown size={14} />
                            </span>
                            <span className="truncate">{group.label}</span>
                            <span className="shrink-0 font-normal text-text-muted">
                              {group.total !== undefined && group.total !== group.rows.length
                                // "showing 12 of 214" — the page is not the group.
                                ? `showing ${group.rows.length} of ${group.total}`
                                : group.rows.length}
                            </span>
                          </button>
                        )
                        : (
                          <span
                            key={column.key}
                            /*
                              * A group row pins the same columns the header and
                              * the rows do. Without this its subtotals scrolled
                              * out from under the pinned columns and reappeared
                              * beneath them — money under Status, which reads as
                              * wrong data rather than as a layout bug.
                              */
                            className={cn(
                              'tnum truncate px-2 text-rowsub text-text-muted',
                              pinned.includes(column.key)
                                && 'sticky z-10 flex h-full items-center bg-surface-group',
                            )}
                            style={pinned.includes(column.key)
                              ? { left: leftOffset(columnIndex) } : undefined}
                          >
                            {column.aggregate?.({ rows: group.rows, groupKey: group.key })}
                          </span>
                        )
                    ))}

                    <span />
                    {(rowMenu || tray) && (
                      <span className="sticky right-0 border-l border-line-soft bg-surface-group" />
                    )}
                  </div>
                )}

                {!isCollapsed && group.rows.map((row) => {
                  const key = rowKey(row)
                  const ticked = selection?.isSelected(key) ?? false
                  /*
                   * **The chevron is per row, not per list.**
                   *
                   * A `tray` that returns nothing for this row means there is
                   * nothing to expand, and a chevron that opens an empty panel
                   * is a promise the row cannot keep. Rowted Admin's activity
                   * list is the case: only the rows that are a visit into a
                   * customer have anything inside them.
                   */
                  const rowTray = tray ? tray(row) : null
                  const isOpen = expanded === key && rowTray !== null

                  return (
                    <Fragment key={key}>
                      <div
                        role="row"
                        data-row={key}
                        tabIndex={0}
                        onFocus={() => setFocused(key)}
                        onKeyDown={(event) => onRowKeyDown(event, row, orderOf.get(key) ?? 0)}
                        onClick={(event) => {
                          // ⌘/ctrl-click is "open in a new tab", which the
                          // screen's own handler does — not a row click.
                          if (event.metaKey || event.ctrlKey) return
                          onRowClick?.(row)
                        }}
                        onContextMenu={rowMenu ? (event) => {
                          event.preventDefault()
                          openMenu(rowMenu(row), event)
                        } : undefined}
                        className={cn(
                          'group grid cursor-pointer items-center border-b border-line-soft bg-surface',
                          'outline-none focus-visible:ring-2 focus-visible:ring-accent-ring',
                          // `bg-hover` is the token; the literal it resolves to was hand-typed here
                          // until the palette guard said so.
                          'hover:bg-hover',
                          (ticked || selectedKey === key) && 'bg-accent-soft hover:bg-accent-soft',
                        )}
                        style={{
                          gridTemplateColumns: template,
                          height: density === 'default' && mark ? undefined : metrics.row,
                          minHeight: metrics.row,
                        }}
                      >
                        {selection && (
                          <div
                            className="sticky z-10 flex h-full items-center justify-center bg-inherit"
                            style={{ left: tickLeft }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Checkbox
                              checked={ticked}
                              onChange={() => undefined}
                              onClick={(event) => tick(row, event)}
                              label="Select this row"
                              hideLabel
                            />
                          </div>
                        )}

                        {mark && (
                          <div
                            className="sticky z-10 flex h-full items-center justify-center self-stretch bg-inherit"
                            style={{ left: markLeft }}
                          >
                            {mark.render(row)}
                          </div>
                        )}

                        {columns.map((column, columnIndex) => (
                          <div
                            key={column.key}
                            data-fit={column.key}
                            className={cn(
                              'px-2',
                              cellFit,
                              metrics.text,
                              column.numeric && 'tnum',
                              pinned.includes(column.key)
                                && 'sticky z-10 h-full bg-inherit flex items-center',
                              drag.draggingKey === column.key && 'opacity-30',
                            )}
                            style={pinned.includes(column.key)
                              ? { left: leftOffset(columnIndex) } : undefined}
                          >
                            {column.render(row)}
                          </div>
                        ))}

                        <div />

                        {(rowMenu || tray) && (
                          <div
                            className="sticky right-0 z-10 flex h-full items-center justify-end gap-0.5 border-l border-line-soft bg-inherit pr-1.5"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {rowTray && (
                              <button
                                type="button"
                                aria-label={isOpen ? 'Close details' : 'Show details'}
                                aria-expanded={isOpen}
                                onClick={() => setExpanded(isOpen ? null : key)}
                                className={cn(
                                  'flex h-7 w-7 items-center justify-center rounded-field text-text-muted',
                                  'hover:bg-line-soft hover:text-text',
                                  isOpen && 'bg-line-soft text-text',
                                )}
                              >
                                <span className={cn('transition-transform', isOpen && 'rotate-90')}>
                                  <IconChevronRight size={15} />
                                </span>
                              </button>
                            )}
                            {rowMenu && (
                              <button
                                type="button"
                                aria-label="More actions"
                                onClick={(event) => openMenu(rowMenu(row), event)}
                                /*
                                 * Always there, not on hover.
                                 *
                                 * Unlike the header's ⋯, which is over the
                                 * label and would eat it: this one has a
                                 * column of its own, so hiding it bought
                                 * nothing and cost every person who did not
                                 * know the menu existed. A control you have to
                                 * discover by accident is a control most
                                 * people never find.
                                 */
                                className="flex h-7 w-7 items-center justify-center rounded-field text-text-muted hover:bg-line-soft hover:text-text"
                              >
                                <IconMore size={15} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {isOpen && rowTray && (
                        <div className="border-b border-line-soft bg-surface-alt">
                          <div
                            className="sticky left-0 px-4 py-3"
                            style={viewport > 0 ? { width: viewport } : undefined}
                          >
                            {rowTray}
                          </div>
                        </div>
                      )}
                    </Fragment>
                  )
                })}
              </Fragment>
            )
          })}

          {/* --- footer ------------------------------------------------- */}
          {showFooter && rows.length > 0 && !loading && (
            <div
              role="row"
              className="sticky bottom-0 z-20 grid h-list-footer items-center border-t border-line bg-surface-alt"
              style={{ gridTemplateColumns: template }}
            >
              {selection && (
                <span aria-hidden className="sticky bg-surface-alt" style={{ left: tickLeft }} />
              )}
              {mark && (
                <span
                  aria-hidden
                  className="sticky bg-surface-alt"
                  style={{ left: markLeft }}
                />
              )}
              {columns.map((column, columnIndex) => (
                <span
                  key={column.key}
                  data-fit={column.key}
                  className={cn(
                    'tnum px-2 text-rowsub font-semibold text-text',
                    cellFit,
                    pinned.includes(column.key)
                      && 'sticky z-10 flex h-full items-center bg-surface-alt',
                  )}
                  style={pinned.includes(column.key)
                    ? { left: leftOffset(columnIndex) } : undefined}
                >
                  {column.aggregate?.({ rows, groupKey: null }) ?? column.footer?.(rows)}
                </span>
              ))}
              <span />
              {(rowMenu || tray) && (
                <span className="sticky right-0 border-l border-line-soft bg-surface-alt" />
              )}
            </div>
          )}
        </div>
      </div>

      {!loading && rows.length === 0 && emptyState}

      {/* The column being dragged, following the pointer. */}
      {drag.ghost && (
        <div
          className="pointer-events-none fixed z-50 rounded-field border border-accent-line bg-surface px-2 py-1.5 text-rowsub font-semibold text-text shadow-popover"
          style={{
            left: drag.ghost.x, top: drag.ghost.y,
            transform: 'translate(-50%, -50%) rotate(1.5deg)',
          }}
        >
          {drag.ghost.label}
        </div>
      )}

      {menu && (
        <ContextMenu items={menu.items} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header cell
// ---------------------------------------------------------------------------

function HeaderCell<T>({
  column, index, pinned, left, width, drag, lastIndex, resize, measuring, onRefit,
  onSort, onMenu,
}: {
  column: DataListColumn<T>
  index: number
  pinned: boolean
  left?: number
  width: number
  drag: ReturnType<typeof useColumnDrag>
  /** So the last header can draw the "drop past the end" line on its right. */
  lastIndex: number
  resize: ReturnType<typeof useColumnResize>
  /** The one pre-paint render the fit measures: nothing truncates. */
  measuring: boolean
  onRefit: () => void
  onSort?: (key: string, additive: boolean) => void
  onMenu?: (event: { clientX: number; clientY: number }) => void
}) {
  const Icon = COLUMN_ICONS[column.icon ?? 'text']
  const sortable = column.sort !== undefined && onSort !== undefined
  const sorted = column.sort ?? null

  return (
    <div
      data-column={column.key}
      data-fit={column.key}
      /*
       * The ⋯ is `absolute`, so it takes no space in the layout and the fit
       * would happily size a column to exactly its label and then draw the
       * button over the last two letters. This says "add room for it" without
       * putting it back into the flow.
       */
      data-fit-reserve={onMenu ? '1' : undefined}
      role="columnheader"
      /*
       * The whole header is labelled, rather than a stray `sr-only` word after
       * the label. v2 asks for the icon's meaning to be in the screen-reader
       * label; as a sibling span it read out as "Name text", which is the icon
       * described rather than the column.
       */
      aria-label={`${column.header}, ${FIELD_ICON_MEANINGS[column.icon ?? 'text']}`}
      aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'group/header relative flex min-w-0 items-center gap-1 bg-surface-alt pl-2 pr-1',
        pinned && 'sticky z-10',
        // The line the column will actually land on — left edge for the slot
        // before this column, right edge for the one after it.
        drag.before === index
          && 'before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-accent',
        drag.before === index + 1 && index === lastIndex
          && 'after:absolute after:inset-y-0 after:right-0 after:w-0.5 after:bg-accent',
        drag.draggingKey === column.key && 'bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgb(var(--line-soft))_4px,rgb(var(--line-soft))_8px)]',
      )}
      style={pinned ? { left } : undefined}
      onContextMenu={onMenu ? (event) => { event.preventDefault(); onMenu(event) } : undefined}
    >
      {/*
        * The icon is the drag handle. v2 is explicit that there is no separate
        * grip: a grip is a second target for the same job, and it took the
        * space the label needed.
        */}
      <span
        role="button"
        tabIndex={-1}
        aria-label={`Move ${column.header}`}
        onPointerDown={(event) => drag.start(event, column.key, column.header)}
        className="shrink-0 cursor-grab text-text-muted active:cursor-grabbing"
      >
        <Icon size={14} />
      </span>

      <button
        type="button"
        disabled={!sortable}
        onClick={(event) => onSort?.(column.key, event.shiftKey)}
        title={sortable ? `Sort by ${column.header}` : column.header}
        className={cn(
          'flex items-center gap-1 text-left',
          measuring ? 'shrink-0' : 'min-w-0 flex-1',
          sortable && 'hover:text-text',
        )}
      >
        <span className={cn('text-label uppercase',
          measuring ? 'whitespace-nowrap' : 'truncate',
          sorted ? 'text-text' : 'text-text-muted')}>
          {column.header}
        </span>

        {sortable && (
          /*
            * Both arrows, always — at 20% until the column is sorted. A column
            * that only shows an arrow once you have clicked it does not look
            * sortable until after you have found out that it is.
            */
          <span aria-hidden className="flex shrink-0 flex-col leading-none">
            <span className={cn('text-[7px]', sorted === 'asc' ? 'text-accent' : 'text-line-strong')}>▲</span>
            <span className={cn('text-[7px]', sorted === 'desc' ? 'text-accent' : 'text-line-strong')}>▼</span>
          </span>
        )}
        {column.sortRank !== undefined && column.sortRank > 0 && (
          <span aria-hidden className="shrink-0 text-[9px] font-bold text-accent">
            {column.sortRank + 1}
          </span>
        )}
        {/*
          * A pinned column says so beside its arrows. Pinning is the one column
          * state with no mark on the column itself: you set it in a menu three
          * inches away and then wonder, two scrolls later, why this one will
          * not move.
          */}
        {pinned && (
          <span className="shrink-0 text-accent" title={`${column.header} is pinned left`}>
            <IconPin size={11} />
            <span className="sr-only">pinned left</span>
          </span>
        )}
        {sorted && (
          <span className="sr-only">
            sorted {sorted === 'asc' ? 'ascending' : 'descending'}
          </span>
        )}
      </button>

      {onMenu && (
        /*
          * Floated over the cell rather than laid out in it. As a flex child it
          * took 16px from every header whether or not it was visible, and the
          * labels truncated to "EXPE…" on a table that had room for them.
          */
        <button
          type="button"
          aria-label={`${column.header} column menu`}
          onClick={(event) => onMenu(event)}
          className="absolute right-1.5 top-1/2 z-10 -translate-y-1/2 rounded-field bg-surface-alt px-0.5 text-text-muted opacity-0 hover:text-text focus-visible:opacity-100 group-hover/header:opacity-100"
        >
          <IconMore size={13} />
        </button>
      )}

      {/*
        * The resize edge. 3px, and it only appears on hover so the header is
        * not a row of handles.
        */}
      <span
        role="separator"
        aria-label={`Resize ${column.header}`}
        onPointerDown={(event) => resize.start(event, column.key, width)}
        onDoubleClick={() => { resize.autoFit(column.key); onRefit() }}
        className="absolute inset-y-0 right-0 w-[3px] cursor-col-resize bg-transparent hover:bg-accent group-hover/header:bg-line-strong"
      />

      {resize.activeKey === column.key && (
        <span className="absolute -bottom-6 right-0 z-40 rounded-field bg-text px-1.5 py-0.5 text-[10px] font-semibold text-surface">
          {resize.liveWidth}px
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Resize
// ---------------------------------------------------------------------------

/**
 * Dragging a column's right edge.
 *
 * Pointer events rather than HTML5 drag: a resize has no drop target, it has a
 * width that follows the pointer, and the browser's drag image would be a
 * translucent picture of the header rather than the column getting wider.
 */
function useColumnResize(onResize?: (key: string, width: number) => void) {
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [liveWidth, setLiveWidth] = useState(0)
  const [local, setLocal] = useState<Record<string, number>>({})
  const from = useRef({ x: 0, width: 0 })

  const start = (event: React.PointerEvent, key: string, width: number) => {
    if (!onResize) return
    event.preventDefault()
    event.stopPropagation()
    ;(event.target as HTMLElement).setPointerCapture(event.pointerId)
    from.current = { x: event.clientX, width }
    setActiveKey(key)
    setLiveWidth(width)
  }

  useEffect(() => {
    if (!activeKey) return

    const move = (event: PointerEvent) => {
      const next = Math.max(MIN_WIDTH, Math.round(from.current.width + event.clientX - from.current.x))
      setLiveWidth(next)
      // Locally while dragging, so the column follows the pointer without a
      // round trip through the view and the URL on every frame.
      setLocal((current) => ({ ...current, [activeKey]: next }))
    }
    const up = () => {
      setActiveKey(null)
      onResize?.(activeKey, Math.max(MIN_WIDTH, liveWidth))
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
  }, [activeKey, liveWidth, onResize])

  return {
    activeKey,
    liveWidth,
    widthOf: (key: string, fallback: number) => local[key] ?? fallback,
    start,
    /** Double-click: back to the column's own idea of its width. */
    autoFit: (key: string) => {
      setLocal((current) => {
        const next = { ...current }
        delete next[key]
        return next
      })
      onResize?.(key, 0)
    },
  }
}

/**
 * Sizing every column to what is in it.
 *
 * Two renders, both before the browser paints, so nothing flashes:
 *
 *   1. lay the grid out with `max-content` columns and the cells un-truncated,
 *      so each cell is exactly as wide as it wants to be;
 *   2. read every cell's box, take the widest per column, and re-render at
 *      those widths.
 *
 * Measured per *cell* rather than by putting `max-content` in one shared
 * template, because there is no shared template: every row is its own grid
 * that happens to use the same string of pixel widths. That is what makes the
 * columns line up, and it is why a column cannot simply be told to size
 * itself.
 *
 * Group rows are left out on purpose. Their first cell holds the group's name
 * and count, which is a label across the table rather than a value in the
 * first column, and fitting to it made Job a 300px column on any grouped view.
 *
 * Widths only ever grow. A fit is re-run when the page, the filter or the sort
 * changes, and a column that shrank back on every page would jump under the
 * pointer all afternoon; growing-only settles after a page or two and then
 * stays put.
 */
function useColumnFit<T>(
  columns: DataListColumn<T>[],
  density: Density,
  rowsKey: string,
) {
  const box = useRef<HTMLDivElement>(null)
  const [measuring, setMeasuring] = useState(true)
  const [fitted, setFitted] = useState<Record<string, number>>({})

  // What the fit is *of*: change the columns or the density and the old
  // numbers mean nothing, so they are thrown away rather than grown from.
  const shape = `${columns.map((column) => column.key).join('|')}#${density}`
  const lastShape = useRef(shape)

  useLayoutEffect(() => { setMeasuring(true) }, [shape, rowsKey])

  useLayoutEffect(() => {
    if (!measuring) return
    const node = box.current
    if (!node) { setMeasuring(false); return }

    const fresh = lastShape.current !== shape
    lastShape.current = shape

    const next: Record<string, number> = fresh ? {} : { ...fitted }
    for (const column of columns) {
      let widest = 0
      const cells = node.querySelectorAll<HTMLElement>(
        `[data-fit="${CSS.escape(column.key)}"]`)
      for (const cell of cells) {
        const wanted = cell.getBoundingClientRect().width
          + (cell.dataset.fitReserve ? HEADER_MENU_RESERVE : 0)
        if (wanted > widest) widest = wanted
      }
      if (widest === 0) continue
      const wanted = Math.min(MAX_FIT, Math.max(MIN_FIT, Math.ceil(widest) + 1))
      next[column.key] = Math.max(next[column.key] ?? 0, wanted)
    }

    setFitted(next)
    setMeasuring(false)
  }, [measuring, shape, columns, fitted])

  return {
    box,
    measuring,
    widthOf: (key: string) => fitted[key],
    /** Double-click on the edge: forget what was measured and measure again. */
    refit: (key: string) => {
      setFitted((current) => {
        const next = { ...current }
        delete next[key]
        return next
      })
      setMeasuring(true)
    },
  }
}

// ---------------------------------------------------------------------------
// Reorder
// ---------------------------------------------------------------------------

/**
 * Dragging a column by its icon.
 *
 * Also pointer events, and for a better reason than resize: v2 asks for the
 * column to lift out as a rotated card with a shadow, for the gap it left to
 * be hatched, and for a green line to mark where it will land. HTML5 drag and
 * drop gives you one screenshot of the element and no say in any of that.
 */
function useColumnDrag<T>(
  columns: DataListColumn<T>[], onReorder?: (key: string, before: number) => void,
) {
  const [draggingKey, setDraggingKey] = useState<string | null>(null)
  /**
   * Where the green line is: *before* the column at this index.
   *
   * A slot rather than a column, which is the fix. The line was drawn on the
   * left edge of whichever header the pointer was over, so the right half of a
   * column meant "before this one" — and the column landed to the left of
   * where the line said it would, every time you dragged rightwards. It also
   * made the slot past the last column unreachable.
   */
  const [before, setBefore] = useState<number | null>(null)
  const [ghost, setGhost] = useState<{ x: number; y: number; label: string } | null>(null)

  const start = (event: React.PointerEvent, key: string, label: string) => {
    if (!onReorder) return
    event.preventDefault()
    setDraggingKey(key)
    setGhost({ x: event.clientX, y: event.clientY, label })
  }

  useEffect(() => {
    if (!draggingKey) return

    const move = (event: PointerEvent) => {
      setGhost((current) => current && { ...current, x: event.clientX, y: event.clientY })

      // Which header the pointer is over, read off the DOM rather than
      // recomputed: the widths are already laid out, and a second model of
      // them would be the one that is wrong.
      const under = document.elementFromPoint(event.clientX, event.clientY)
        ?.closest<HTMLElement>('[data-column]')
      if (!under) return
      const index = columns.findIndex((column) => column.key === under.dataset.column)
      if (index < 0) return

      // Past the middle of a header means the slot after it.
      const box = under.getBoundingClientRect()
      setBefore(event.clientX > box.left + box.width / 2 ? index + 1 : index)
    }

    const up = () => {
      if (before !== null && draggingKey) onReorder?.(draggingKey, before)
      setDraggingKey(null)
      setBefore(null)
      setGhost(null)
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
  }, [draggingKey, before, columns, onReorder])

  return { draggingKey, before, ghost, start }
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

/** Grey bars where the rows will be. Never a spinner over the table (v2). */
function Skeleton({
  columns, template, metrics,
}: { columns: number; template: string; metrics: { row: number } }) {
  return (
    <div aria-hidden>
      {Array.from({ length: 8 }, (_, row) => (
        <div
          key={row}
          className="grid items-center border-b border-line-soft bg-surface"
          style={{ gridTemplateColumns: template, height: metrics.row }}
        >
          <span />
          {Array.from({ length: columns }, (_, column) => (
            <span key={column} className="mx-2 h-2.5 rounded-tag bg-line-soft"
              style={{ opacity: 1 - row * 0.09 }} />
          ))}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The card, for a phone
// ---------------------------------------------------------------------------

/**
 * Press and hold to select — the gesture a phone has instead of a tick box.
 *
 * A list on a phone shows no checkboxes until there is something to check:
 * eight columns of chrome for an action most people take once a week is the
 * kind of thing that makes an app feel like a spreadsheet someone shrank. So
 * the first selection is made by holding a card, and from then on every card
 * shows its box and a tap picks it up.
 *
 * The details are what make it feel right rather than broken:
 *
 *   - **450ms**, which is long enough not to fire while somebody is starting
 *     a scroll and short enough that nobody wonders whether it worked.
 *   - **Movement cancels it.** A finger that has travelled 8px is a scroll,
 *     and a list that selects a row when you try to scroll past it is worse
 *     than a list with no selection at all.
 *   - **The click after is swallowed.** The browser still sends one on lift,
 *     and without the guard every long press also opened the record it had
 *     just selected.
 *   - **The context menu is suppressed** for the length of the press: a long
 *     press on a touch screen raises one by default, and the app's own menu
 *     lives on the ⋯ button.
 */
function useLongPress(onLongPress?: () => void) {
  const timer = useRef<number | null>(null)
  const from = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)

  const stop = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
    from.current = null
  }, [])

  useEffect(() => stop, [stop])

  return {
    /** True when the last press became a hold, so the click can be dropped. */
    fired,
    handlers: onLongPress
      ? {
        onPointerDown: (event: React.PointerEvent) => {
          // A mouse has a right button for this; holding one down is not a
          // gesture anybody makes on purpose.
          if (event.pointerType === 'mouse') return
          fired.current = false
          from.current = { x: event.clientX, y: event.clientY }
          timer.current = window.setTimeout(() => {
            fired.current = true
            onLongPress()
            navigator.vibrate?.(8)
          }, 450)
        },
        onPointerMove: (event: React.PointerEvent) => {
          const start = from.current
          if (!start) return
          if (Math.abs(event.clientX - start.x) > 8 || Math.abs(event.clientY - start.y) > 8) {
            stop()
          }
        },
        onPointerUp: stop,
        onPointerCancel: stop,
        onContextMenu: (event: React.MouseEvent) => {
          if (timer.current !== null || fired.current) event.preventDefault()
        },
      }
      : {},
  }
}

/**
 * One row of the list, as a card.
 *
 * Three rows of content, in the order somebody reads them: what this is, what
 * it belongs to, and how it stands. §3.3 puts touch targets at 44px, which is
 * why the ⋯ is a 40px square in the corner rather than the 28px button the
 * table uses.
 */
function RowCard<T>({
  rowId, row, title, chip, meta, trailing, line, mark, ticked, selecting,
  onToggle, onOpen, onMenu,
}: {
  rowId: string
  row: T
  title?: DataListColumn<T>
  chip?: DataListColumn<T>
  meta: DataListColumn<T>[]
  trailing?: DataListColumn<T>
  /** The screen's own third row, which replaces the three above (§8). */
  line?: (row: T) => CardLine | null
  mark?: { render: (row: T) => ReactNode }
  ticked: boolean
  selecting: boolean
  onToggle?: () => void
  onOpen?: () => void
  onMenu?: (event: { clientX: number; clientY: number }) => void
}) {
  const press = useLongPress(onToggle)

  /*
   * What a column has to say about this row, or nothing.
   *
   * A table draws `—` in an empty cell because the column heading above it
   * still names what is missing (plan §3.5). A card has no headings, so a lone
   * em dash floating on the third row is a dash and not an absence — and a
   * contact with no last-contact date got a whole empty line drawn for it. So
   * an empty value is dropped here, and a third row with nothing in it is not
   * drawn at all.
   */
  const valueOf = (column?: DataListColumn<T>): ReactNode => {
    if (!column) return null
    const node = column.render(row)
    return node === EMPTY_CELL || node === '' || node === null || node === undefined
      ? null
      : node
  }

  const written = line?.(row) ?? null
  const chipValue = written === null ? valueOf(chip) : null
  const metaValues = written === null
    ? meta
      .map((column) => ({ key: column.key, numeric: column.numeric, node: valueOf(column) }))
      .filter((entry) => entry.node !== null)
    : []
  const trailingValue = written === null ? valueOf(trailing) : null
  const hasFacts = chipValue !== null || metaValues.length > 0 || trailingValue !== null

  /* The parts of a written row that actually have something in them. */
  const writtenFacts = (written?.facts ?? [])
    .filter((fact) => fact !== null && fact !== undefined && fact !== '')
  const hasWritten = written !== null
    && (written.chip != null || writtenFacts.length > 0 || written.trailing != null)

  return (
    <div
      role="row"
      data-row={rowId}
      tabIndex={0}
      aria-selected={ticked}
      onClick={() => {
        // The click the browser sends after a hold is the hold, not a tap.
        if (press.fired.current) { press.fired.current = false; return }
        if (selecting && onToggle) onToggle()
        else onOpen?.()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') { event.preventDefault(); onOpen?.() }
        if (event.key === ' ') { event.preventDefault(); onToggle?.() }
      }}
      {...press.handlers}
      className={cn(
        'flex min-w-0 overflow-hidden rounded-card border bg-surface text-left',
        'outline-none focus-visible:ring-2 focus-visible:ring-accent-ring',
        // A hold is a hold, not a text selection: without this the first
        // thing a long press does on a phone is highlight the name.
        'select-none [-webkit-touch-callout:none]',
        ticked ? 'border-accent-line bg-accent-soft' : 'border-line',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 py-2.5 pl-3 pr-1.5">
        <div className="flex items-start gap-2.5">
          {selecting && onToggle && (
            <span className="shrink-0 pt-0.5" onClick={(event) => event.stopPropagation()}>
              <Checkbox
                checked={ticked}
                onChange={() => undefined}
                onClick={onToggle}
                label="Select this row"
                hideLabel
              />
            </span>
          )}
          {/*
            * **A box, not a bare mark.**
            *
            * The marks are not one kind of element: a contact's is an
            * `Avatar`, a property's an `<svg>`, a visit's an `inline-flex`
            * calendar card. Dropped straight into a `<span>` they are all
            * inline-level, so each one sits on its parent's text baseline
            * with a descender's worth of space under it — and how much
            * depends on the element. That is why the visit mark read as
            * sitting noticeably lower than the rest while the others looked
            * "subtly off" from each other.
            *
            * A fixed 28px flex box makes every mark a block-level child of a
            * box of known size, so the glyph starts at the same distance
            * from the top and the left of the card on every list in the
            * product — which is the whole of what a mark column is for.
            */}
          {!selecting && mark && (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center">
              {mark.render(row)}
            </span>
          )}

          {/*
            * The name, at 15px. The column renders a `CellStack`, which is
            * already a bold line over a muted one — so rows one and two of
            * the card are the column doing what it always does, in a bigger
            * size than a table cell would give it.
            */}
          <div className="min-w-0 flex-1 text-[15px] leading-snug">
            {title?.render(row)}
          </div>

          {onMenu && (
            <button
              type="button"
              aria-label="More actions"
              onClick={(event) => { event.stopPropagation(); onMenu(event) }}
              className="-mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-field text-text-muted"
            >
              <IconMore size={18} />
            </button>
          )}
        </div>

        {/*
          * **Row three: the chip, the facts, the date** (v3 §8).
          *
          * One layout for every list, because this is where a card says what
          * state a record is in and when it last did anything — and a chip
          * that is 12px on one screen and right-aligned on another is five
          * screens' worth of small decisions nobody made on purpose.
          *
          * The facts truncate as a group and the chip and the date do not:
          * a status and a date are short and fixed, a company name is
          * neither, so the only part that can overflow is the only part
          * allowed to.
          */}
        {hasWritten && written && (
          <div className="flex min-w-0 items-center gap-2 pr-1.5 text-rowsub text-text-muted">
            {written.chip != null && <span className="shrink-0">{written.chip}</span>}
            {writtenFacts.length > 0 && (
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                {writtenFacts.map((fact, index) => (
                  <Fragment key={index}>
                    {index > 0 && (
                      <i aria-hidden
                        className="h-[3px] w-[3px] shrink-0 rounded-full bg-line-strong" />
                    )}
                    {/*
                      * **Each fact truncates, not the group.**
                      *
                      * The ellipsis property does not apply to a flex item,
                      * so `truncate` on the row simply clipped the last word
                      * mid-letter — "Grounds cha" rather than "Grounds cha…".
                      * On the part itself it is an ordinary block again and
                      * the ellipsis comes back, which is the difference
                      * between a name that is too long and a name that looks
                      * like a rendering fault.
                      *
                      * (Named in words rather than written out, because the
                      * token guard reads this file as source and a hyphenated
                      * CSS property is shaped exactly like a utility it does
                      * not recognise.)
                      */}
                    <span className="min-w-0 truncate">{fact}</span>
                  </Fragment>
                ))}
              </span>
            )}
            {written.trailing != null && (
              <span className="tnum ml-auto shrink-0 whitespace-nowrap pl-1">
                {written.trailing}
              </span>
            )}
          </div>
        )}

        {hasFacts && (
          <div className="flex min-w-0 items-center gap-2 pr-1.5 text-rowsub text-text-muted">
            {chipValue !== null && <span className="shrink-0">{chipValue}</span>}
            {metaValues.map((entry) => (
              <span key={entry.key} className={cn('min-w-0 truncate', entry.numeric && 'tnum')}>
                {entry.node}
              </span>
            ))}
            {trailingValue !== null && (
              <span className={cn('ml-auto shrink-0 whitespace-nowrap',
                trailing?.numeric && 'tnum')}>
                {trailingValue}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** Eight card-shaped grey blocks, so a first load is not a blank screen. */
function CardSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-2">
      {Array.from({ length: 8 }, (_, row) => (
        <div
          key={row}
          className="h-[86px] rounded-card border border-line bg-surface"
          style={{ opacity: 1 - row * 0.09 }}
        />
      ))}
    </div>
  )
}

/** A bold primary line over an 11.5px muted second line (v2 principle 2). */
export function CellStack({ primary, secondary }: { primary: ReactNode; secondary?: ReactNode }) {
  return (
    <span className="flex min-w-0 flex-col justify-center leading-tight">
      <span className="truncate font-semibold text-text">{primary}</span>
      {secondary !== undefined && secondary !== null && secondary !== '' && (
        <span className="truncate text-rowsub text-text-muted">{secondary}</span>
      )}
    </span>
  )
}

/** What an empty cell reads as. Plan §3.5: an em dash, never a blank. */
export const EMPTY_CELL = '—'

/**
 * The 20px caret beside the header checkbox.
 *
 * Its own component because the popover it opens is the *list's* menu, handed
 * down as a node — `DataList` has no idea what selecting all that match
 * means, and should not: it draws a table.
 */
function SelectionCaret({ menu }: { menu: ReactNode }) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  return (
    <div ref={anchor} className="relative">
      <button
        type="button"
        aria-label="Selection choices"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex h-5 w-5 items-center justify-center rounded-field text-text-faint hover:bg-line-soft hover:text-text"
      >
        <IconChevronDown size={12} />
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        label="Selection choices"
        align="left"
        width={280}
        anchor={anchor.current}
        className="rounded-menu"
      >
        {menu}
      </Popover>
    </div>
  )
}
